import { EChartsOption } from 'echarts';
import * as echarts from 'echarts/core';
import { dataViewFormatter, decimalScoreCellRenderer, getTimestampedFileNameForWidget } from '../helpers/widgets';
import { getWaterColour } from '../helpers/rongo-colours';

interface WaiQualityData {
  dominatWaterColour: string;
  waterClarity: {
    value: number;
    name: string;
  };
  riparianCoverageText: {
    value: number;
    name: string;
  };
  taongaWaiSpeciesAbundance: {
    value: number;
    name: string;
  };
}

interface WaiQualityDataByDate {
  [date: string]: WaiQualityData;
}

/**
 * Should be 5 colours max, but just in case an extra 3
 * selected by rank attribute
 */
const WaiQualityColours: { [key: string]: { primary: string; light: string } } = {
  waterClarity: {
    primary: '#0582bf',
    light: '#00b1d8',
  },
  riparianCoverageText: {
    primary: '#00916f',
    light: '#86cdae',
  },
  taongaWai: {
    primary: '#c83c7b',
    light: '#ee4891',
  },
};

const getWaiQualityChartConfig = (data: WaiQualityDataByDate): EChartsOption => {
  if (data === undefined) {
    const cfg: EChartsOption = {};
    return cfg;
  }

  // Prepare x-axis and series data
  const xAxisDates = Object.keys(data);
  const waterClarityValues = xAxisDates.map((date) => data[date].waterClarity.value);
  const riparianCoverageValues = xAxisDates.map((date) => data[date].riparianCoverageText.value);
  const taongaWaiSpeciesAbundanceValues = xAxisDates.map((date) => data[date].taongaWaiSpeciesAbundance.value);

  const cfg: EChartsOption = {
    color: [
      WaiQualityColours.waterClarity.primary,
      WaiQualityColours.riparianCoverageText.primary,
      WaiQualityColours.taongaWai.primary,
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      formatter: (params: any) => {
        const date = params[0].axisValue;
        const quality = data[date];

        let tooltipHtml = '';

        const makeRow = (label: string, value: string, color: string) =>
          `<span style="display:inline-block;margin-right:6px;width:10px;height:10px;border-radius:50%;background:${color}"></span>
          ${label}: <strong>${value}</strong><br/>`;

        if (quality) {
          tooltipHtml += makeRow('Water Clarity', quality.waterClarity.name, params[0]?.color);
          tooltipHtml += makeRow('Riparian Coverage', quality.riparianCoverageText.name, params[1]?.color);
          tooltipHtml += makeRow('Taonga Wai Abundance', quality.taongaWaiSpeciesAbundance.name, params[2]?.color);

          const dominantColour = getWaterColour(quality.dominatWaterColour);
          tooltipHtml += `<br/><em>Dominant Water Colour:<br/><strong style="color: ${dominantColour}">${quality.dominatWaterColour}</strong></em><br/>`;
        }

        return tooltipHtml;
      },
    },
    legend: {
      data: ['Water Clarity', 'Riparian Coverage', 'Taonga Wai Abundance'],
    },
    toolbox: {
      top: '20px',
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement =>
            dataViewFormatter(option as EChartsOption, decimalScoreCellRenderer),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('rongo-wai-quality'),
        },
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
        data: xAxisDates,
      },
    ],
    yAxis: [
      {
        type: 'value',
        show: false,
      },
    ],
    series: [
      {
        name: 'Water Clarity',
        type: 'line',
        smooth: true,
        stack: 'total',
        lineStyle: {
          width: 0,
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: WaiQualityColours.waterClarity.primary },
            { offset: 1, color: WaiQualityColours.waterClarity.light },
          ]),
        },
        emphasis: { focus: 'series' },
        data: waterClarityValues,
      },
      {
        name: 'Riparian Coverage',
        type: 'line',
        smooth: true,
        stack: 'total',
        lineStyle: {
          width: 0,
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: WaiQualityColours.riparianCoverageText.primary },
            { offset: 1, color: WaiQualityColours.riparianCoverageText.light },
          ]),
        },
        emphasis: { focus: 'series' },
        data: riparianCoverageValues,
      },
      {
        name: 'Taonga Wai Abundance',
        type: 'line',
        smooth: true,
        stack: 'total',
        lineStyle: {
          width: 0,
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: WaiQualityColours.taongaWai.primary },
            { offset: 1, color: WaiQualityColours.taongaWai.light },
          ]),
        },
        emphasis: { focus: 'series' },
        data: taongaWaiSpeciesAbundanceValues,
      },
    ],
  };
  return cfg;
};

export { getWaiQualityChartConfig, WaiQualityColours, WaiQualityDataByDate, WaiQualityData };
